import { NgForOf, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslocoModule } from '@ngneat/transloco';
import { OrderType, PaymentType, PaymentTypeIconPipe, PaymentTypeLabelPipe } from '@us/web/shared/data-access';
import { TuiGroupModule, TuiSvgModule } from '@taiga-ui/core';
import { TuiAvatarModule, TuiRadioBlockModule } from '@taiga-ui/kit';
import { TuiDestroyService } from '@taiga-ui/cdk';
import { filter, first, takeUntil, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectBankTransferInformation, selectPaymentSettingsMpos } from '@us/web/shop/data-access';

@Component({
    selector: 'us-payment-type-options',
    standalone: true,
    imports: [
        TranslocoModule,
        PaymentTypeLabelPipe,
        PaymentTypeIconPipe,
        FormsModule,
        NgForOf,
        TuiSvgModule,
        TuiGroupModule,
        TuiRadioBlockModule,
        ReactiveFormsModule,
        TuiAvatarModule,
        NgIf
    ],
    providers: [TuiDestroyService],
    templateUrl: './payment-type-options.component.html',
    styleUrls: ['./payment-type-options.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaymentTypeOptionsComponent implements OnInit {
    @Input() options: PaymentType[] = [];
    @Input() orderType!: OrderType;
    @Input() value: PaymentType | null = null;
    @Output() valueChange: EventEmitter<PaymentType> = new EventEmitter<PaymentType>();
    @Input() size: 'm' | 'l' = 'l';

    private readonly store = inject(Store);
    readonly bankTransferInformation = this.store.selectSignal(selectBankTransferInformation);

    private readonly destroy$ = inject(TuiDestroyService, { self: true });

    readonly PaymentType = PaymentType;

    readonly paymentTypeCtrl = new FormControl<PaymentType | null>(null);
    isHiddenIconLeft = false;

    ngOnInit() {
        if (this.value && !this.options.includes(this.value)) {
            this.value = this.options[0];
            this.paymentTypeCtrl.patchValue(this.value);
            setTimeout(() => {
                if (this.value !== null) {
                    this.valueChange.emit(this.value);
                }
            }, 600);
        } else {
            this.paymentTypeCtrl.patchValue(this.value);
        }

        this.paymentTypeCtrl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                filter(value => value !== null),
                tap(value => this.valueChange.emit(value as PaymentType))
            )
            .subscribe();

        this.store.select(selectPaymentSettingsMpos).pipe(
            takeUntil(this.destroy$)
        ).subscribe(res => {
             const serviceNames = (res?.serviceNames || []).map((item: any) => item.key);
             if (!serviceNames.includes('CREATE_QR') && this.options.includes(PaymentType.Mpos)) {
                const index = this.options.findIndex((item) => item === PaymentType.Mpos);
                if (index !== -1) {
                    this.options.splice(index, 1);
                }
             }
        });
    }

    onSelect() {
        if (this.value !== null) {
            this.valueChange.emit(this.value);
        }
    }

    onScroll(event: any) {
        event.stopPropagation();
        const scrolledWidth = event.target.scrollLeft + event.target.clientWidth;
        this.isHiddenIconLeft = scrolledWidth >= event.target.scrollWidth;
    }
}
